/**
 * Решения менеджера по кампании — в шапке страницы работы
 * (`/cycles/:id/works/:workId`, номер работы в адресе — номер кампании).
 *
 * Одно главное действие — «Перенести», остальное в меню «⋯»: распространить на
 * новые поля, выбывшие поля, приостановить / возобновить, завершить, отменить.
 * Окно решения — общее `DecisionDialog` экрана «Кампании» (импортом, без
 * правок). Кнопки видны, только если сервер разрешил запись (`can_write`) и
 * кампания ещё открыта; хоким и наблюдатель шапку без действий.
 */

import { useState } from 'react';
import { useAuth } from '../../../auth/context';
import { useRequest } from '../../../api/useRequest';
import { defineMessages, type PlainMessage } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { RowMenu, type RowMenuItem } from '../../ui/forms';
import { DecisionDialog, type DecisionKind } from '../ops/campaigns/DecisionDialog';
import { deskApi, type DeskCampaign } from '../ops/campaigns/desk';
import './agrocycle.css';

const m = defineMessages({
  reschedule: { id: 'cycle.work.action.reschedule', defaultMessage: 'Перенести', description: 'Главная кнопка в шапке страницы работы (менеджер): перенести срок кампании; коротко' },
  menu: { id: 'cycle.work.action.menu', defaultMessage: 'Другие решения по кампании', description: 'aria-label кнопки «⋯» в шапке страницы работы (менеджер): меню остальных решений по кампании' },
  extend: { id: 'cycle.work.action.extend', defaultMessage: 'Распространить на новые поля', description: 'Пункт меню в шапке страницы работы (менеджер): добавить в кампанию поля, появившиеся позже' },
  extendNote: { id: 'cycle.work.action.extendNote', defaultMessage: 'новых полей: {count}', description: 'Вторая строка пункта «Распространить»; {count} — число полей вне кампании' },
  retire: { id: 'cycle.work.action.retire', defaultMessage: 'Выбывшие поля…', description: 'Пункт меню в шапке страницы работы (менеджер): решить судьбу работ по полям, вышедшим из агроцикла' },
  retireNote: { id: 'cycle.work.action.retireNote', defaultMessage: 'вышли из агроцикла: {count}', description: 'Вторая строка пункта «Выбывшие поля»; {count} — число полей' },
  pause: { id: 'cycle.work.action.pause', defaultMessage: 'Приостановить', description: 'Пункт меню в шапке страницы работы (менеджер): приостановить кампанию' },
  resume: { id: 'cycle.work.action.resume', defaultMessage: 'Возобновить', description: 'Пункт меню в шапке страницы работы (менеджер): возобновить приостановленную кампанию' },
  complete: { id: 'cycle.work.action.complete', defaultMessage: 'Завершить', description: 'Пункт меню в шапке страницы работы (менеджер): завершить кампанию' },
  cancel: { id: 'cycle.work.action.cancel', defaultMessage: 'Отменить кампанию', description: 'Пункт меню в шапке страницы работы (менеджер): отменить кампанию' },
  doneReschedule: { id: 'cycle.work.done.reschedule', defaultMessage: 'Срок кампании перенесён.', description: 'Уведомление на странице работы после переноса кампании' },
  doneExtend: { id: 'cycle.work.done.extend', defaultMessage: 'Кампания распространена на новые поля.', description: 'Уведомление на странице работы после распространения кампании на новые поля' },
  doneRetire: { id: 'cycle.work.done.retire', defaultMessage: 'Работы по выбывшим полям отменены.', description: 'Уведомление на странице работы после отмены работ по выбывшим полям' },
  donePause: { id: 'cycle.work.done.pause', defaultMessage: 'Кампания приостановлена.', description: 'Уведомление на странице работы после приостановки кампании' },
  doneResume: { id: 'cycle.work.done.resume', defaultMessage: 'Кампания возобновлена.', description: 'Уведомление на странице работы после возобновления кампании' },
  doneComplete: { id: 'cycle.work.done.complete', defaultMessage: 'Кампания завершена.', description: 'Уведомление на странице работы после завершения кампании' },
  doneCancel: { id: 'cycle.work.done.cancel', defaultMessage: 'Кампания отменена.', description: 'Уведомление на странице работы после отмены кампании' },
});

const DONE: Record<DecisionKind, PlainMessage> = {
  reschedule: m.doneReschedule,
  extend: m.doneExtend,
  retire: m.doneRetire,
  pause: m.donePause,
  resume: m.doneResume,
  complete: m.doneComplete,
  cancel: m.doneCancel,
};

export function CampaignActions({ cycleKey, campaignId, onDone }: { cycleKey: string; campaignId: number; onDone: (message: string) => void }) {
  const t = useT();
  const mayManage = useAuth().principal?.can_manage_campaigns ?? false;
  // Запрос только у тех, кому записи вообще разрешены: хоким страницу работы не нагружает.
  const desk = useRequest(() => (mayManage ? deskApi.desk(cycleKey, true) : Promise.resolve(null)), [mayManage, cycleKey]);
  const [deciding, setDeciding] = useState<DecisionKind | null>(null);

  const campaign: DeskCampaign | null = desk.data?.campaigns.find((row) => row.id === campaignId) ?? null;
  if (!mayManage || !desk.data?.can_write || !campaign) return null;
  if (campaign.status === 'completed' || campaign.status === 'cancelled') return null;

  const decide = (kind: DecisionKind) => () => setDeciding(kind);
  const items: RowMenuItem[] = [
    ...(campaign.outside_fields > 0 ? [{ id: 'extend', label: `${t(m.extend)} · ${t(m.extendNote, { count: campaign.outside_fields })}`, onAction: decide('extend') }] : []),
    ...(campaign.departed > 0 ? [{ id: 'retire', label: `${t(m.retire)} · ${t(m.retireNote, { count: campaign.departed })}`, onAction: decide('retire') }] : []),
    campaign.status === 'paused' ? { id: 'resume', label: t(m.resume), onAction: decide('resume') } : { id: 'pause', label: t(m.pause), onAction: decide('pause') },
    { id: 'complete', label: t(m.complete), onAction: decide('complete') },
    { id: 'cancel', label: t(m.cancel), tone: 'danger', separatorBefore: true, onAction: decide('cancel') },
  ];

  return (
    <>
      <div className="agc-actions-bar" data-guide="work-actions">
        <button type="button" className="astra-button astra-button-primary" onClick={() => setDeciding('reschedule')}>
          {t(m.reschedule)}
        </button>
        <RowMenu aria-label={t(m.menu)} items={items} />
      </div>
      {deciding ? (
        <DecisionDialog
          kind={deciding}
          campaign={campaign}
          onClose={() => setDeciding(null)}
          onDone={() => {
            const kind = deciding;
            setDeciding(null);
            onDone(t(DONE[kind]));
          }}
        />
      ) : null}
    </>
  );
}
